import React, {
	PropsWithChildren,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { cloneDeep } from "lodash";
import { LatLngLiteral } from "leaflet";
import { Option } from "uikit";
import { useTranslation } from "react-i18next";

import { Popup } from "../../../../../common";
import { Map } from "../../../../../../services";
import {
	HandleEventsProps,
	useGetCoordinatesByHouse,
	useGetSector,
	useHandleEvents,
	ValueAccessors,
} from "../../hooks";
import cityToString from "../utils/cityToString";
import streetToString from "../utils/streetToString";
import {
	buildHouseSelectOptions,
	pickHouseSelectOption,
} from "../utils/houseSelectOptions";
import { StyledInput, StyledPopup } from "../styled";
import { OverlayHouse } from "../Overlays";
import PointOrderModalTableBase from "../..";
import { cleanSettlementText } from "../utils/normolize";

const HouseBase: React.FC<HouseBase.Props> = ({
	rowData,
	rowIndex,
	dataKey,
	colIndex,
	setData,
	styles,
	offset,
	street,
	height,
	handleEventsOptions,
	containerId,
	addHouseCoordinates,
	countRow,
	reloadCell,
	setReloadCell,
	setLoadingChangePoint,
	taxiServiceId,
}) => {
	const { t } = useTranslation();
	const idCel = useMemo(() => rowData.raw.id, [rowData.raw.id]);
	const isLastRow = useMemo(() => idCel === countRow, [countRow, idCel]);
	const pointOnMap = useMemo(() => t("point_on_map") ?? "", [t]);
	const {
		inputValue,
		open,
		onChangeInputValue,
		onEnterToInput,
		onExistFromInput,
		onClickToInput,
		onFocusToInput,
		onBlurCaptureToInput,
		setColIndex,
		setDataKey,
		setInputId,
		setRowId,
		setRowIndex,
		save,
		setSave,
		reset,
		nextFocus,
		setOpen,
		debounceClose,
		setHasHouse,
		nextElementById,
		debounceNextElementByIdZero,
		setLoading,
		setInputValue,
	} = useHandleEvents(
		{
			idCel,
			isLastRow,
		},
		handleEventsOptions,
	);

	const { get: getSector, addSector } = useGetSector();
	const {
		get,
		response,
		house,
		reset: resetHouse,
	} = useGetCoordinatesByHouse();
	const [activeId, setActiveOption] = useState<number>(-2);
	const [isNewInput, setIsNewInput] = useState<boolean>(false);
	const [isSelect, setIsSelect] = useState<boolean>(false);

	useEffect(() => {
		if (!rowData.raw.type) reset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rowData.raw.type]);

	useEffect(() => {
		setColIndex(`${colIndex}`);
		setDataKey(dataKey);
		setInputId(`${idCel + dataKey}`);
		setRowId(idCel);
		setRowIndex(`${rowIndex}`);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const updatedData = useCallback(
		async (params: Map.Search.Object, queryHouse: string | null) => {
			const optionsType: Map.Search.Type[] = [
				"favorite",
				"object",
				"localObject",
			];

			const type = optionsType.includes(params.type)
				? "address"
				: params.type;
			const entrance = optionsType.includes(params.type)
				? params.entrance
				: "";

			const sector = await getSector(params.coordinates, {
				taxiServiceId,
			});

			const city = cityToString(params);
			const streetName = streetToString(params);
			const cloneRowData = cloneDeep(rowData);
			const oldCoordinates = `${cloneRowData?.coordinates?.lat}${cloneRowData?.coordinates?.lng}`;
			const newData = addSector(cloneRowData, sector);
			addHouseCoordinates(params.coordinates, newData.raw.id);

			// base
			newData.city = city;
			newData.street = streetName;
			newData.type = type;
			newData.coordinates = params.coordinates;

			newData.name = params.name || "";
			newData.house =
				params?.searchType === "google_street_address"
					? String(params?.number)
					: params.number || queryHouse || "";
			newData.customHouse = params.customHouse || "";
			newData.customStreet = params.customStreet || "";

			newData.title = params.title || "";
			newData.save = true;
			newData.raw.save = true;
			newData.saveType = "house";
			newData.raw.saveType = "house";
			// raw
			newData.raw.type = type;
			newData.raw.coordinates = params.coordinates;
			newData.raw.country = params.country;
			newData.raw.countryCode = params.countryCode;

			newData.raw.region = params.region || "";
			newData.raw.district = params.district || "";

			newData.raw.name = params.name || params.title || "";
			newData.raw.customHouse = params.customHouse || "";
			newData.raw.customStreet = params.customStreet || "";

			newData.raw.street = params.street || params.customStreet || "";
			newData.raw.streetType = params.streetType || "";

			newData.raw.settlement = params.settlement || "";
			newData.raw.settlementType = params.settlementType || "";

			newData.raw.house =
				params?.searchType === "google_street_address"
					? String(params?.number)
					: queryHouse || "";

			// clear
			newData.entrance = entrance || "";
			newData.polygon = "";
			newData.flat = "";
			// clear raw
			newData.raw.entrance = entrance || "";
			newData.raw.flat = "";

			const newCoordinates = `${params.coordinates?.lat}${params.coordinates?.lng}`;
			const isValidation =
				cleanSettlementText(rowData.raw.settlement) ===
					cleanSettlementText(newData.raw.settlement) &&
				rowData.raw.settlementType === newData.raw.settlementType &&
				cleanSettlementText(rowData.raw.street) ===
					cleanSettlementText(newData.raw.street) &&
				rowData.raw.streetType === newData.raw.streetType &&
				rowData.raw.type === newData.raw.type &&
				rowData.raw.house === newData.raw.house &&
				cleanSettlementText(rowData.raw.name) ===
					cleanSettlementText(newData.raw.name) &&
				oldCoordinates === newCoordinates;

			// reset
			if (
				isValidation &&
				params?.searchType !== "google_street_address"
			) {
				setOpen(false);
				debounceClose();
				resetHouse();
				setIsNewInput(false);
				setIsSelect(false);
				setActiveOption(-2);
				return;
			}

			setData(newData);
			reset();
			debounceClose();
			setReloadCell(`${idCel}house`);
			if (save) setSave(false);
			resetHouse();
			setIsNewInput(false);
			setIsSelect(false);
			setActiveOption(-2);
		},
		[
			taxiServiceId,
			addHouseCoordinates,
			addSector,
			debounceClose,
			getSector,
			reset,
			rowData,
			save,
			setData,
			setOpen,
			setSave,
			setReloadCell,
			idCel,
			resetHouse,
		],
	);

	const updatedDataClear = useCallback(() => {
		setInputValue(null);
		reset();
		resetHouse();
		setReloadCell("");
		setSave(false);
		setIsNewInput(false);
		setIsSelect(false);
		if (rowData?.raw?.house) rowData.raw.house = "";
	}, [setInputValue, reset, resetHouse, setReloadCell, setSave, rowData.raw]);

	useEffect(() => {
		if (inputValue && isNewInput && isSelect) {
			setIsNewInput(false);
		}
		if (rowData?.raw?.house) {
			setIsNewInput(false);
		}
	}, [inputValue, isNewInput, isSelect, rowData?.raw?.house]);

	const isDisabled = useMemo(() => {
		const list: boolean[] = [
			rowData?.raw?.street === pointOnMap &&
				!street?.houses?.length &&
				!street?.house,
			!rowData?.raw?.street && !street?.houses?.length && !street?.house,
			// eslint-disable-next-line regex/invalid
			rowData?.raw?.street === "центр",
			// eslint-disable-next-line regex/invalid
			rowData?.raw?.street === "центр",
			rowData?.raw?.street === "center",
		];

		return list.includes(true);
	}, [
		pointOnMap,
		rowData?.raw?.street,
		street?.houses?.length,
		street?.house,
	]);

	useEffect(() => {
		const hasChosenHouse = Boolean(
			String(rowData?.raw?.house || rowData?.house || "").trim(),
		);

		const isOsmNominatim =
			street?.searchType === "osm_nominatim" ||
			(rowData?.raw as { searchType?: string } | undefined)
				?.searchType === "osm_nominatim";

		const skipHousePickListAfterStreet =
			hasChosenHouse && (isOsmNominatim || street?.type === "address");

		if (
			reloadCell === `${idCel}street` ||
			reloadCell === `${idCel}settlement` ||
			isDisabled
		) {
			if (skipHousePickListAfterStreet) {
				// OSM або готовий address з номером: без повторного вибору з streetHouses.
				// Do not run updatedDataClear — він обнуляє raw.house і відкриває UI будинку.
				setReloadCell("");
				reset();
				resetHouse();
				setSave(false);
				setIsNewInput(false);
				setIsSelect(false);
				setOpen(false);
			} else {
				updatedDataClear();
			}
		}
		if (
			(reloadCell === `${idCel}street` ||
				reloadCell === `${idCel}settlement`) &&
			street?.houses?.length &&
			!skipHousePickListAfterStreet
		) {
			setTimeout(() => {
				setActiveOption(0);
				setOpen(true);
			}, 200);
		}
	}, [
		reloadCell,
		updatedDataClear,
		isDisabled,
		rowData,
		idCel,
		street?.houses?.length,
		street?.searchType,
		street?.type,
		setOpen,
		reset,
		resetHouse,
		setReloadCell,
		setSave,
		setIsNewInput,
		setIsSelect,
	]);

	useEffect(() => {
		if (response) {
			updatedData(response, house);
		}
	}, [response, updatedData, house, resetHouse, inputValue]);

	useEffect(() => {
		if (inputValue && activeId === -1) setActiveOption(0);
	}, [activeId, inputValue]);

	const propsInput = useMemo(() => {
		const house = rowData.raw?.house;
		const number = rowData.raw?.number;
		const baseValue = `${house || number || ""}`;

		return {
			value: inputValue !== null ? inputValue : baseValue,
		};
	}, [inputValue, rowData.raw?.house, rowData.raw?.number]);

	useEffect(() => {
		if (propsInput.value) setHasHouse(true);
		else setHasHouse(false);
	}, [propsInput.value, setHasHouse]);

	useEffect(() => {
		const optionsType: Map.Search.Type[] = [
			"favorite",
			"object",
			"localObject",
		];

		if (
			street?.type &&
			(optionsType.includes(street?.type) || street?.type === "address")
		) {
			setLoading(false);
			const streetHouse =
				street?.house || street?.number || street?.customHouse || "";
			if (street?.type === "address" && !String(streetHouse).trim()) {
				return;
			}
			get(street, streetHouse);

			if (handleEventsOptions?.fol) {
				nextElementById(handleEventsOptions?.fol);
			} else {
				nextFocus();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [street, handleEventsOptions?.fol]);

	const onSelect = useCallback(
		(option: Option<string>) => {
			setLoading(false);
			setIsSelect(true);
			setIsNewInput(false);
			if (handleEventsOptions?.externalElement) {
				debounceNextElementByIdZero(
					handleEventsOptions?.externalElement,
				);
			} else {
				nextFocus();
			}
			if (street) {
				if (option && option?.key !== "query") {
					get(street, option.value);
				} else if (option && option?.key === "query") {
					get(street, option.value);
				} else if (inputValue) {
					get(street, inputValue);
				}
				return;
			}
			if (!street && option && rowData.raw) {
				get(
					{
						coordinates: rowData.raw.coordinates,
						houses: [],
						street: rowData.raw.street || "",
						streetType: rowData.raw.streetType || "",
						settlement: rowData.raw.settlement || "",
						settlementType: rowData.raw.settlementType || "",
					},
					option.value,
				);
			}
			setIsSelect(true);
			setIsNewInput(false);
		},

		[
			setLoading,
			handleEventsOptions?.externalElement,
			street,
			rowData.raw,
			debounceNextElementByIdZero,
			nextFocus,
			inputValue,
			get,
		],
	);

	const openOverlay = useMemo(() => open, [open]);
	const size = useMemo(
		() =>
			propsInput.value.length > 5 ? propsInput.value.length * 1.25 : 5,
		[propsInput.value.length],
	);

	useEffect(() => {
		if (street?.houses?.length && rowData.saveType === "city") {
			setTimeout(() => {
				setActiveOption(0);
				setOpen(true);
			}, 200);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<StyledPopup
			useClickControl
			useArrow={false}
			open={openOverlay}
			offset={offset}
			styles={styles}
			trackerId={dataKey + idCel}
			containerId={containerId}
			onChangeOpen={setOpen}
			tracker={
				<StyledInput
					size={size}
					height={height}
					b={isNewInput ? "1px solid red" : ""}
					id={`${idCel + dataKey}`}
					aria-label={dataKey}
					aria-rowindex={rowIndex}
					aria-colindex={colIndex}
					autoComplete="one-time-code"
					onChange={(event) => {
						if (isDisabled) return;
						setIsNewInput(true);
						onChangeInputValue(event);
						setIsSelect(false);
						const { target } = event;
						const { value } = target as unknown as any;

						if (value) setActiveOption(0);
					}}
					onKeyUp={(event) => {
						const keyEvent = event.key;
						const isConfirmKey =
							keyEvent === "Enter" || keyEvent === "Tab";

						if (
							!isConfirmKey &&
							!rowData?.raw?.house &&
							Array.isArray(street?.houses) &&
							street?.houses?.length
						) {
							setIsNewInput(true);
							setIsSelect(false);
						}
						const { target } = event;
						const { value } = target as unknown as any;

						const list = ["Enter", "Tab"];
						if (
							!value &&
							list.includes(event.key) &&
							!rowData?.raw?.house &&
							Array.isArray(street?.houses) &&
							street?.houses?.length
						) {
							return;
						}

						onEnterToInput(event);
					}}
					onKeyDown={(event) => {
						const { target } = event;
						const { value } = target as unknown as any;
						const list = ["Enter", "Tab"];
						const keyEvent = event.key;

						if (
							list.includes(keyEvent) &&
							activeId < 0 &&
							rowData?.raw?.house &&
							rowData.raw.house === value
						) {
							nextFocus();
							event.stopPropagation();
							event.preventDefault();
							return;
						}

						if (street?.houses?.length) {
							const list2 = ["ArrowLeft", "ArrowRight"];
							if (
								list.includes(keyEvent) &&
								(open || Boolean(value))
							) {
								const option = pickHouseSelectOption(
									buildHouseSelectOptions(
										street.houses,
										value || "",
									),
									activeId,
								);
								if (option) {
									setLoadingChangePoint?.(true);
									onSelect(option);
								} else if (value && keyEvent === "Enter") {
									setLoadingChangePoint?.(true);
									onSelect({
										key: "query",
										label: value,
										value,
									});
								}
								event.preventDefault();
								event.stopPropagation();
								return;
							}
							if (activeId === -2 && keyEvent === "ArrowDown") {
								if (keyEvent === "ArrowDown")
									setActiveOption(-1);
								return;
							}

							if (
								activeId >= -2 &&
								!isSelect &&
								!rowData?.raw?.house &&
								list2.includes(keyEvent)
							) {
								return;
							}
						}

						if (list.includes(keyEvent)) {
							setLoadingChangePoint?.(true);
						}
						onExistFromInput(event);
					}}
					onClick={onClickToInput}
					onFocus={onFocusToInput}
					onBlurCapture={onBlurCaptureToInput}
					onCut={(event) => {
						event.preventDefault();
					}}
					onDragOver={(event) => {
						event.preventDefault();
					}}
					onDrop={(event) => {
						event.preventDefault();
					}}
					{...propsInput}
				/>
			}
		>
			{openOverlay && (
				<OverlayHouse
					data={street}
					activeId={activeId}
					houseQuery={inputValue || ""}
					setActiveOption={setActiveOption}
					textSelectOnSelect={onSelect}
				/>
			)}
		</StyledPopup>
	);
};

declare namespace HouseBase {
	interface Props extends PropsWithChildren {
		rowData: PointOrderModalTableBase.OrderModal;
		setData: (data: PointOrderModalTableBase.OrderModal) => void;
		/** Street with house to selection */
		street?: Map.Search.Object;
		addHouseCoordinates: (
			coordinates: LatLngLiteral | undefined,
			id: number,
		) => void;
		reset?: (id: number) => void;

		rowIndex?: number;
		dataKey: ValueAccessors | string;
		colIndex: number;

		styles?: Popup.Styles;
		offset?: Popup.Props["offset"];
		height?: number;
		customerId?: number;
		handleEventsOptions?: HandleEventsProps;
		containerId?: string;
		countRow?: number;
		reloadCell: string;
		setReloadCell: React.Dispatch<React.SetStateAction<string>>;
		loadingChangePoint?: boolean;
		setLoadingChangePoint: React.Dispatch<React.SetStateAction<boolean>>;
		taxiServiceId?: number;
	}
}

export const House = memo(HouseBase);

export default HouseBase;
